import React, { useState, useEffect } from 'react';
import './AuditorCorner.css';

// API Configuration - Use relative URLs for same-domain requests
const API_BASE_URL = '';

const AuditorCorner = ({ onClose, onViewAnalysis }) => {
  const [analyses, setAnalyses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const analysesPerPage = 25;

  // Load analyses list
  useEffect(() => {
    const fetchAnalyses = async () => {
      try {
        setLoading(true);
        const sessionToken = localStorage.getItem('session_token');
        const response = await fetch(`${API_BASE_URL}/auditor/analyses`, {
          headers: {
            'Authorization': `Bearer ${sessionToken}`
          }
        });
        if (!response.ok) {
          throw new Error('Failed to fetch analyses');
        }
        const data = await response.json();
        setAnalyses(data.analyses || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalyses();
  }, []);

  // Filter analyses based on search term
  const filteredAnalyses = analyses.filter(analysis =>
    analysis.analysis_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    analysis.analysis_date.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Pagination
  const totalPages = Math.ceil(filteredAnalyses.length / analysesPerPage);
  const startIndex = (currentPage - 1) * analysesPerPage;
  const endIndex = startIndex + analysesPerPage;
  const currentAnalyses = filteredAnalyses.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleViewAnalysis = (jobId) => {
    onViewAnalysis(jobId);
  };

  if (loading) {
    return (
      <div className="auditor-corner-modal">
        <div className="auditor-corner-content">
          <div className="auditor-corner-header">
            <h2>🔍 Auditor's Corner</h2>
            <button className="close-button" onClick={onClose}>×</button>
          </div>
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading analyses...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="auditor-corner-modal">
        <div className="auditor-corner-content">
          <div className="auditor-corner-header">
            <h2>🔍 Auditor's Corner</h2>
            <button className="close-button" onClick={onClose}>×</button>
          </div>
          <div className="error-container">
            <p className="error-message">⚠️ {error}</p>
            <button onClick={() => window.location.reload()} className="retry-button">
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auditor-corner-modal">
      <div className="auditor-corner-content">
        <div className="auditor-corner-header">
          <h2>🔍 Auditor's Corner</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="auditor-corner-body">
          <div className="search-section">
            <input
              type="text"
              placeholder="Search analyses by name or date..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="search-input"
            />
          </div>

          <div className="analyses-table-container">
            <table className="analyses-table">
              <thead>
                <tr>
                  <th>Analysis Name</th>
                  <th>Analysis Date</th>
                  <th>Total Controls</th>
                  <th>Total Corrections</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentAnalyses.length > 0 ? (
                  currentAnalyses.map((analysis) => (
                    <tr key={analysis.job_id}>
                      <td>
                        <button
                          className="analysis-name-link"
                          onClick={() => handleViewAnalysis(analysis.job_id)}
                        >
                          {analysis.analysis_name}
                        </button>
                      </td>
                      <td>{analysis.analysis_date}</td>
                      <td>{analysis.total_controls}</td>
                      <td>
                        <span className={`corrections-count ${analysis.total_corrections > 0 ? 'has-corrections' : 'no-corrections'}`}>
                          {analysis.total_corrections}
                        </span>
                      </td>
                      <td>
                        <button
                          className="view-button"
                          onClick={() => handleViewAnalysis(analysis.job_id)}
                        >
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="no-results">
                      {searchTerm ? 'No analyses found matching your search.' : 'No completed analyses found.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="pagination-button"
              >
                Previous
              </button>
              
              <span className="pagination-info">
                Page {currentPage} of {totalPages} ({filteredAnalyses.length} total analyses)
              </span>
              
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="pagination-button"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuditorCorner; 